import React, { useState, useEffect } from 'react';
import { Search, Filter, X, ChevronDown } from 'lucide-react';
import WheelLoader from '../components/WheelLoader';
import '../styles/Category.css';

const VehicleFilters = () => {
  const [filters, setFilters] = useState({
    marca: '',
    modelo: '',
    año: ''
  });
  const [activeFilters, setActiveFilters] = useState(0);
  
  // Estados para resultados y paginación
  const [vehiculos, setVehiculos] = useState([]);
  const [skip, setSkip] = useState(0);
  const limit = 7;
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Estados para opciones de filtros
  const [marcas, setMarcas] = useState([]);
  const [loadingMarcas, setLoadingMarcas] = useState(true);

  // API URL
  const API_URL = (process.env.REACT_APP_API_URL || '').replace(/^http:/, 'https:');

  // Opciones de filtros estáticos
  const filterOptions = {
    año: ['2024', '2023', '2022', '2021', '2020', '2019', '2018', '2017', '2016', '2015']
  };

  // Función para obtener marcas del backend
  const fetchMarcas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/marca/`);
      if (!res.ok) throw new Error(`Error al obtener marcas: ${res.status}`);
      
      const data = await res.json();
      setMarcas(data || []);
    } catch (err) {
      console.error('Error al cargar marcas:', err);
      setError('Error al cargar las marcas de vehículos');
    } finally {
      setLoadingMarcas(false);
    }
  };

  // Cargar marcas al montar el componente
  useEffect(() => {
    fetchMarcas();
  }, []);

  // Función para buscar vehículos
  const fetchVehiculos = async (resetResults = false) => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      // Construir parámetros de búsqueda
      const params = new URLSearchParams();
      params.set('skip', resetResults ? '0' : skip.toString());
      params.set('limit', limit.toString());

      if (filters.marca) {
        // Buscar el ID de la marca seleccionada
        const marcaSeleccionada = marcas.find(m => m.nombre_marca_vehiculo === filters.marca);
        if (marcaSeleccionada) {
          params.set('marca', marcaSeleccionada.id_marca_vehiculo.toString());
        }
      }
      if (filters.año) params.set('año', filters.año);
      if (filters.modelo && filters.modelo.trim()) params.set('modelo', filters.modelo.trim());

      const res = await fetch(`${API_URL}/api/v1/publicacion/?${params}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      
      const data = await res.json();
      if (!data.publicaciones) throw new Error('Datos inválidos recibidos del backend');

      if (resetResults) {
        // Primera búsqueda o nuevos filtros
        setVehiculos(data.publicaciones);
        setSkip(limit);
      } else {
        // Cargar más resultados
        setVehiculos(prev => [
          ...prev,
          ...data.publicaciones.filter(veh => !prev.some(v => v.id === veh.id))
        ]);
        setSkip(prev => prev + limit);
      }

      setTotal(data.total || 0);
      setHasSearched(true);

    } catch (err) {
      console.error('Error al buscar vehículos:', err);
      setError('No se pudo realizar la búsqueda. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    const activeCount = Object.values(newFilters).filter(val => val !== '').length;
    setActiveFilters(activeCount);
  };

  const clearFilter = (filterType) => {
    handleFilterChange(filterType, '');
  };

  const clearAllFilters = () => {
    setFilters({ marca: '', modelo: '', año: '' });
    setActiveFilters(0);
    setVehiculos([]);
    setHasSearched(false);
    setSkip(0);
    setTotal(0);
  };

  const handleSearch = () => {
    setSkip(0);
    fetchVehiculos(true); // Reset results = true
  };

  const handleVerMas = () => {
    fetchVehiculos(false); // Reset results = false
  };

  return (
    <div className="vehicle-filters-page">
      <main className="main-container">
        <div className="filters-header">
          <div className="filters-info">
            {activeFilters > 0 && (
              <div className="filters-counter">
                <button onClick={clearAllFilters}><X size={16} /> Limpiar todo</button>
              </div>
            )}
          </div>
        </div>

        <div className="filters-box">
          <div className="filters-title">
            <Filter size={20} />
            <h2>Filtros de Búsqueda</h2>
          </div>

          <div className="filters-grid">
            {['marca', 'modelo', 'año'].map((key) => (
              <div key={key} className="filter-item">
                <label>{key.charAt(0).toUpperCase() + key.slice(1)}</label>
                <div className="input-wrapper">
                  {key === 'modelo' ? (
                    <input
                      type="text"
                      placeholder="Buscar modelo... (ej: GR 86)"
                      value={filters[key]}
                      onChange={(e) => handleFilterChange(key, e.target.value)}
                    />
                  ) : (
                    <select
                      value={filters[key]}
                      onChange={(e) => handleFilterChange(key, e.target.value)}
                      disabled={key === 'marca' && loadingMarcas}
                    >
                      <option value="">
                        {key === 'marca' && loadingMarcas 
                          ? 'Cargando marcas...' 
                          : `Seleccionar ${key}`
                        }
                      </option>
                      {key === 'marca' 
                        ? marcas.map(marca => (
                            <option key={marca.id_marca_vehiculo} value={marca.nombre_marca_vehiculo}>
                              {marca.nombre_marca_vehiculo}
                            </option>
                          ))
                        : filterOptions[key]?.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))
                      }
                    </select>
                  )}
                  {filters[key] && (
                    <button onClick={() => clearFilter(key)} className="clear-btn">
                      <X size={16} />
                    </button>
                  )}
                  {key !== 'modelo' && <ChevronDown className="dropdown-icon" size={16} />}
                </div>
              </div>
            ))}
          </div>

          <div className="search-button-container">
            <button onClick={handleSearch} disabled={loading}>
              <Search size={18} /> {loading ? 'Buscando...' : 'Buscar Vehículos'}
            </button>
          </div>
        </div>

        {activeFilters > 0 && (
          <div className="applied-filters-box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Filtros Aplicados</h3>
            </div>
            <div className="applied-tags">
              {Object.entries(filters).map(([key, value]) =>
                value && (
                  <span key={key} className="tag">
                    <span>{key}:</span>
                    <span>{value}</span>
                    <button onClick={() => clearFilter(key)}>
                      <X size={12} />
                    </button>
                  </span>
                )
              )}
            </div>
          </div>
        )}

        {/* Resultados */}
        {hasSearched && (
          <div className="results-section">
            {vehiculos.length > 0 ? (
              <>
                <div className="results-header">
                  <h3>Resultados de Búsqueda ({total} vehículo{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''})</h3>
                </div>
                
                {/* Grid de vehículos (similar al home) */}
                <section className="cars-grid">
                  {vehiculos.map(vehiculo => (
                    <div key={vehiculo.id} className="car-card">
                      {/* Imagen clickeable */}
                      <a href={`/post/${vehiculo.id}`} style={{ display: 'block' }}>
                        <img
                          className="car-image"
                          src={vehiculo.url_portada || '/cars/default.jpg'}
                          alt={vehiculo.titulo}
                        />
                      </a>
                      <div className="car-content">
                        <h2 className="car-title">{vehiculo.titulo}</h2>
                        <p className="car-description">{vehiculo.descripcion_corta}</p>
                      
                        
                        <div className="car-meta">
                          <span className="car-date">
                            Publicado: {new Date(vehiculo.fecha_publicacion).toLocaleDateString()}
                          </span>
                          <a className="read-more" href={`/post/${vehiculo.id}`}>
                            Ver detalles
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </section>
                
                {/* Loading */}
                {loading && (
                  <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                    <WheelLoader />
                  </div>
                )}
                
                {/* Botón "Ver más" */}
                {!loading && skip < total && (
                  <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                    <button onClick={handleVerMas} className="ver-mas-btn">
                      Ver más vehículos
                    </button>
                  </div>
                )}
              </>
            ) : (
              !loading && (
                <div className="no-results">
                  <Search size={48} />
                  <h3>No se encontraron vehículos</h3>
                  <p>No hay vehículos que coincidan con los filtros seleccionados. Intenta con otros criterios.</p>
                </div>
              )
            )}
          </div>
        )}

        {/* Estado inicial */}
        {!hasSearched && !loading && (
          <div className="results-placeholder">
            <Search size={48} />
            <h3>Resultados de Búsqueda</h3>
            <p>Los vehículos que coincidan con tus filtros aparecerán aquí</p>
          </div>
        )}

        {/* Error */}
        {error && <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>}
      </main>
    </div>
  );
};

export default VehicleFilters;
